import { observable, action, computed } from 'mobx'
import { LocationApi, LocationModels } from '@getgreenline/locations'

export type ActiveFilter = 'all' | 'grouped' | 'ungrouped'

export class LocationGroupStore {
  @observable banners: LocationModels.IBannerContract[] = []
  @observable locationGroups: Map<number, LocationModels.ILocationGroupContract[]> = new Map()
  @observable isLoading = false
  @observable searchQuery = ''
  @observable activeFilter: ActiveFilter = 'all'
  @observable expandedBannerIds: Set<number> = new Set()
  @observable expandedGroupIds: Set<number> = new Set()

  @computed
  get groupedLocationIds(): Set<number> {
    const ids = new Set<number>()
    this.banners?.forEach((banner) => {
      banner.locations?.forEach((loc) => ids.add(loc.id))
    })
    return ids
  }

  @computed
  get groupedCount(): number {
    return this.groupedLocationIds.size
  }

  @computed
  get filteredBanners(): LocationModels.IBannerContract[] {
    if (!this.searchQuery) {
      return this.banners
    }
    const query = this.searchQuery.toLowerCase()
    return this.banners.filter((banner) => banner.name.toLowerCase().includes(query))
  }

  @action
  setSearchQuery = (query: string) => {
    this.searchQuery = query
  }

  @action
  setActiveFilter = (filter: ActiveFilter) => {
    this.activeFilter = filter
  }

  @action
  markBannerExpanded = (bannerId: number) => {
    this.expandedBannerIds = new Set(this.expandedBannerIds).add(bannerId)
  }

  @action
  markGroupExpanded = (groupId: number) => {
    this.expandedGroupIds = new Set(this.expandedGroupIds).add(groupId)
  }

  @action
  consumeBannerExpand = (bannerId: number): boolean => {
    if (!this.expandedBannerIds.has(bannerId)) return false
    const next = new Set(this.expandedBannerIds)
    next.delete(bannerId)
    this.expandedBannerIds = next
    return true
  }

  @action
  consumeGroupExpand = (groupId: number): boolean => {
    if (!this.expandedGroupIds.has(groupId)) return false
    const next = new Set(this.expandedGroupIds)
    next.delete(groupId)
    this.expandedGroupIds = next
    return true
  }

  @action
  loadBanners = async (organizationId: number) => {
    this.isLoading = true
    try {
      const response = await LocationApi.listBanners(organizationId, { limit: 100, offset: 0 })
      this.banners = response.data
    } finally {
      this.isLoading = false
    }
  }

  @action
  loadLocationGroups = async (bannerId?: number) => {
    try {
      const filter = { limit: 100, ...(bannerId !== undefined && { bannerId }) }
      const response = await LocationApi.listLocationGroups(filter)
      const groups = response.data
      const groupsByBanner = new Map<number, LocationModels.ILocationGroupContract[]>()
      groups.forEach((group) => {
        const existing = groupsByBanner.get(group.bannerId) || []
        groupsByBanner.set(group.bannerId, [...existing, group])
      })
      if (bannerId !== undefined) {
        this.locationGroups.set(bannerId, groupsByBanner.get(bannerId) ?? [])
      } else {
        this.locationGroups = groupsByBanner
      }
    } catch (error) {
      console.error('Failed to load location groups', error)
    }
  }

  @action
  createBanner = async (
    organizationId: number,
    data: LocationModels.ICreateBannerContract,
  ): Promise<LocationModels.IBannerContract> => {
    try {
      const newBanner = await LocationApi.createBanner(organizationId, data)
      this.banners = [...this.banners, newBanner]
      return newBanner
    } catch (error) {
      console.error('Failed to create banner', error)
      throw error
    }
  }

  @action
  updateBanner = async (
    organizationId: number,
    bannerId: number,
    data: LocationModels.IUpdateBannerContract,
  ): Promise<LocationModels.IBannerContract> => {
    try {
      const updated = await LocationApi.updateBanner(organizationId, bannerId, data)
      this.banners = this.banners.map((b) => (b.id === bannerId ? updated : b))
      return updated
    } catch (error) {
      console.error('Failed to update banner', error)
      throw error
    }
  }

  @action
  deleteBanner = async (organizationId: number, bannerId: number): Promise<void> => {
    try {
      await LocationApi.deleteBanner(organizationId, bannerId)
      this.banners = this.banners.filter((b) => b.id !== bannerId)
      this.locationGroups.delete(bannerId)
    } catch (error) {
      console.error('Failed to delete banner', error)
      throw error
    }
  }

  @action
  createLocationGroup = async (
    data: LocationModels.ICreateLocationGroupContract,
  ): Promise<LocationModels.ILocationGroupContract> => {
    try {
      const newGroup = await LocationApi.createLocationGroup(data)
      const existing = this.locationGroups.get(data.bannerId) || []
      this.locationGroups.set(data.bannerId, [...existing, newGroup])
      return newGroup
    } catch (error) {
      console.error('Failed to create location group', error)
      throw error
    }
  }

  @action
  updateLocationGroup = async (
    locationGroupId: number,
    data: LocationModels.IUpdateLocationGroupContract,
  ): Promise<LocationModels.ILocationGroupContract> => {
    try {
      const updated = await LocationApi.updateLocationGroup(locationGroupId, data)
      const groups = this.locationGroups.get(data.bannerId) || []
      this.locationGroups.set(
        data.bannerId,
        groups.map((g) => (g.id === locationGroupId ? { ...g, ...updated } : g)),
      )
      return updated
    } catch (error) {
      console.error('Failed to update location group', error)
      throw error
    }
  }

  @action
  deleteLocationGroup = async (locationGroupId: number, bannerId: number): Promise<void> => {
    try {
      await LocationApi.deleteLocationGroup(locationGroupId)
      const groups = this.locationGroups.get(bannerId) || []
      this.locationGroups.set(
        bannerId,
        groups.filter((g) => g.id !== locationGroupId),
      )
    } catch (error) {
      console.error('Failed to delete location group', error)
      throw error
    }
  }

  getFilteredBanners = (
    allLocations: LocationModels.ILocationContract[],
    companyNameMap: Map<number, string>,
  ): LocationModels.IBannerContract[] => {
    if (!this.searchQuery) return this.banners

    const query = this.searchQuery.toLowerCase()
    return this.banners.filter((banner) => {
      if (banner.name.toLowerCase().includes(query)) return true

      const bannerLocationIds = new Set(banner.locations?.map((loc) => loc.id) ?? [])
      const bannerLocations = allLocations.filter((loc) => bannerLocationIds.has(loc.id))

      if (bannerLocations.some((loc) => loc.name.toLowerCase().includes(query))) return true

      return bannerLocations.some((loc) => {
        const companyName = companyNameMap.get(loc.companyId)
        return companyName?.toLowerCase().includes(query)
      })
    })
  }

  getUngroupedLocations = (
    allLocations: LocationModels.ILocationContract[],
  ): LocationModels.ILocationContract[] => {
    const groupedIds = this.groupedLocationIds
    return allLocations.filter((loc) => !loc.isHidden && !groupedIds.has(loc.id))
  }

  getGroupedLocations = (
    allLocations: LocationModels.ILocationContract[],
  ): LocationModels.ILocationContract[] => {
    const groupedIds = this.groupedLocationIds
    return allLocations.filter((loc) => !loc.isHidden && groupedIds.has(loc.id))
  }
}
